/**
 * Split view content for topping up the account balance.
 * Displays current balance, preset/custom top-up amounts, redeem code input,
 * and mirrors the options available on the balance settings page.
 */
import { GiftIcon, Loader2Icon, PlusIcon, WalletIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import type { BillingBalance } from '@/components/billing/billing.interface'
import { BillingBalanceDisplay } from '@/components/billing/billing-balance-display.atom'
import { BillingCustomAmountInput } from '@/components/billing/billing-custom-amount-input.atom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

export interface TopUpBalanceContentProps {
  balance: BillingBalance
  selectedAmount: number | null
  customAmount: string
  isValidAmount: boolean
  validationError: string | null
  onPresetClick: (amount: number) => void
  onCustomAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onTopUp: () => void
  isTopUpLoading?: boolean
  redeemCode: string
  isValidRedeemCode: boolean
  onRedeemCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onRedeem: () => void
  isRedeemLoading?: boolean
  className?: string
}

const PRESET_AMOUNT_LIST = [10, 50, 100, 200]

const TopUpHeader: React.FC = (): ReactNode => (
  <div className="flex flex-col gap-3 text-center">
    <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/5">
      <WalletIcon className="size-8 text-primary" />
    </div>
    <div className="flex flex-col gap-1">
      <h2 className="text-xl font-semibold">Top Up Balance</h2>
      <p className="text-sm text-muted-foreground">
        Add credits to continue using AI agents and tools.
      </p>
    </div>
  </div>
)

interface BalanceSectionProps {
  balance: BillingBalance
}

const BalanceSection: React.FC<BalanceSectionProps> = ({ balance }): ReactNode => (
  <div className="flex flex-col gap-2 rounded-lg bg-muted/50 p-4 text-center">
    <span className="text-xs font-medium text-muted-foreground">Current Balance</span>
    <BillingBalanceDisplay amount={balance.amount} currency={balance.currency} size="lg" />
  </div>
)

interface PresetAmountGridProps {
  selectedAmount: number | null
  onSelect: (amount: number) => void
}

const PresetAmountGrid: React.FC<PresetAmountGridProps> = ({
  selectedAmount,
  onSelect,
}): ReactNode => (
  <div className="flex flex-col gap-2">
    <Label className="text-sm">Select amount</Label>
    <div className="top-up-preset-grid grid grid-cols-4 gap-2">
      {PRESET_AMOUNT_LIST.map((amount) => (
        <Button
          key={amount}
          variant={selectedAmount === amount ? 'default' : 'outline'}
          className="top-up-preset-button"
          onClick={() => onSelect(amount)}
        >
          ${amount}
        </Button>
      ))}
    </div>
  </div>
)

interface TopUpButtonProps {
  currentAmount: number | null
  isLoading: boolean
  isValid: boolean
  onClick: () => void
}

const TopUpButton: React.FC<TopUpButtonProps> = ({
  currentAmount,
  isLoading,
  isValid,
  onClick,
}): ReactNode => (
  <Button
    onClick={onClick}
    disabled={!isValid || isLoading}
    size="lg"
    className="top-up-balance-button w-full"
  >
    {isLoading ? (
      <>
        <Loader2Icon className="mr-2 size-4 animate-spin" />
        Processing...
      </>
    ) : (
      <>
        <PlusIcon className="mr-2 size-4" />
        Add ${currentAmount ?? 0}
      </>
    )}
  </Button>
)

interface RedeemCodeSectionProps {
  code: string
  isValidCode: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onRedeem: () => void
  isLoading: boolean
}

const RedeemCodeSection: React.FC<RedeemCodeSectionProps> = ({
  code,
  isValidCode,
  onChange,
  onRedeem,
  isLoading,
}): ReactNode => (
  <div className="flex flex-col gap-3">
    <div className="flex items-center gap-2">
      <GiftIcon className="size-4 text-muted-foreground" />
      <span className="text-sm font-medium">Redeem Code</span>
    </div>
    <div className="flex gap-2">
      <Input
        type="text"
        placeholder="Enter code"
        value={code}
        onChange={onChange}
        className="top-up-redeem-input font-mono uppercase"
        autoComplete="off"
        disabled={isLoading}
      />
      <Button
        onClick={onRedeem}
        disabled={!isValidCode || isLoading}
        variant="outline"
        className="top-up-redeem-button"
      >
        {isLoading ? <Loader2Icon className="size-4 animate-spin" /> : 'Redeem'}
      </Button>
    </div>
  </div>
)

export const TopUpBalanceContent: React.FC<TopUpBalanceContentProps> = ({
  balance,
  selectedAmount,
  customAmount,
  isValidAmount,
  validationError,
  onPresetClick,
  onCustomAmountChange,
  onTopUp,
  isTopUpLoading = false,
  redeemCode,
  isValidRedeemCode,
  onRedeemCodeChange,
  onRedeem,
  isRedeemLoading = false,
  className,
}): ReactNode => {
  const currentAmount = customAmount ? Number.parseInt(customAmount, 10) : selectedAmount

  return (
    <ScrollArea className={cn('top-up-balance-content h-full', className)}>
      <div className="flex flex-col gap-6 p-6">
        <TopUpHeader />

        <BalanceSection balance={balance} />

        <Separator />

        <PresetAmountGrid selectedAmount={selectedAmount} onSelect={onPresetClick} />

        <BillingCustomAmountInput
          id="top-up-custom-amount"
          value={customAmount}
          onChange={onCustomAmountChange}
          error={validationError}
          inputClassName="top-up-custom-input"
        />

        <TopUpButton
          currentAmount={currentAmount}
          isLoading={isTopUpLoading}
          isValid={isValidAmount}
          onClick={onTopUp}
        />

        <Separator />

        <RedeemCodeSection
          code={redeemCode}
          isValidCode={isValidRedeemCode}
          onChange={onRedeemCodeChange}
          onRedeem={onRedeem}
          isLoading={isRedeemLoading}
        />
      </div>
    </ScrollArea>
  )
}
